import { useRef, useEffect, useMemo } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import { Appointment } from '../types/appointment'
import { Patient } from '../types/patient'
import { User } from '../types/user'
import { formatPatientName, formatUserName } from '../utils/name'
import './AppointmentCalendar.css'

interface AppointmentCalendarProps {
  appointments: Appointment[]
  patients: Patient[]
  staff: User[]
  onDateClick?: (date: Date) => void
  onEventClick?: (appointment: Appointment) => void
}

export function AppointmentCalendar({
  appointments = [],
  patients,
  staff,
  onDateClick,
  onEventClick,
}: AppointmentCalendarProps) {
  const calendarRef = useRef<FullCalendar>(null)

  // Filter out cancelled and DNA appointments for calendar display
  // This ensures cancelled/DNA appointments are immediately removed from the calendar
  // and their slots become available again
  const activeAppointments = useMemo(() => {
    if (!appointments || appointments.length === 0) return []
    return appointments.filter((a) => a.status !== 'cancelled' && a.status !== 'dna')
  }, [appointments])

  // Transform appointments to FullCalendar events
  const events = useMemo(() => {
    return activeAppointments.map((appointment) => {
    const patient = patients.find((p) => p.id === appointment.patientId)
    const staffMember = staff.find((s) => s.id === appointment.staffId)
    
    const patientName = patient ? formatPatientName(patient as any) : 'Unknown Patient'
    const staffName = staffMember ? formatUserName(staffMember as any) : 'Unknown Staff'

    // Combine date and time
    const startDateTime = `${appointment.appointmentDate}T${appointment.startTime}:00`
    const endDateTime = `${appointment.appointmentDate}T${appointment.endTime}:00`

    return {
      id: appointment.id,
      title: `${patientName} - ${staffName}`,
      start: startDateTime,
      end: endDateTime,
      backgroundColor: getStatusColor(appointment.status),
      borderColor: getStatusColor(appointment.status),
      extendedProps: {
        appointment,
        patient,
        staffMember,
      },
    }
  })
  }, [activeAppointments, patients, staff])

  // Create a stable key based on appointment IDs and statuses to detect changes
  const appointmentsKey = useMemo(() => {
    return appointments.map(a => `${a.id}:${a.status}`).sort().join(',')
  }, [appointments])

  // Update calendar events when they change
  // This handles real-time updates from polling (every 5 seconds) and immediate local changes
  // It syncs the FullCalendar display with the appointments data without remounting the calendar
  useEffect(() => {
    if (!calendarRef.current) return
    
    const calendarApi = calendarRef.current.getApi()
    
    // Get current event IDs
    const currentEventIds = new Set(
      calendarApi.getEvents().map(event => event.id)
    )
    
    // Get new event IDs
    const newEventIds = new Set(events.map(event => event.id))
    
    // Remove events that are no longer in the new list (e.g., cancelled appointments)
    currentEventIds.forEach(eventId => {
      if (!newEventIds.has(eventId)) {
        const event = calendarApi.getEventById(eventId)
        if (event) {
          event.remove()
        }
      }
    })
    
    // Add or update events
    events.forEach(eventData => {
      const existingEvent = calendarApi.getEventById(eventData.id)
      if (existingEvent) {
        // Update existing event (handles changes from other users)
        existingEvent.setProp('title', eventData.title)
        existingEvent.setStart(eventData.start)
        existingEvent.setEnd(eventData.end)
        existingEvent.setProp('backgroundColor', eventData.backgroundColor)
        existingEvent.setProp('borderColor', eventData.borderColor)
        existingEvent.setExtendedProp('appointment', eventData.extendedProps.appointment)
      } else {
        // Add new event (handles new appointments from other users)
        calendarApi.addEvent(eventData)
      }
    })
  }, [events, appointmentsKey]) // Use appointmentsKey to detect any appointment changes


  const handleDateClick = (arg: any) => {
    if (onDateClick) {
      onDateClick(arg.date)
    }
  }

  const handleEventClick = (arg: any) => {
    const appointment = arg.event.extendedProps.appointment as Appointment
    if (!appointment) return

    if (onEventClick) {
      onEventClick(appointment)
    }
  }

  return (
    <div className="appointment-calendar">
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="timeGridWeek"
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        }}
        events={events}
        dateClick={handleDateClick}
        eventClick={handleEventClick}
        editable={false}
        selectable={true}
        selectMirror={true}
        dayMaxEvents={true}
        weekends={true}
        slotMinTime="08:00:00"
        slotMaxTime="18:00:00"
        slotDuration="00:30:00"
        height="auto"
        eventTimeFormat={{
          hour: '2-digit',
          minute: '2-digit',
          meridiem: 'short',
        }}
      />
    </div>
  )
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'scheduled':
      return '#3b82f6' // blue
    case 'arrived':
      return '#22c55e' // bright green for arrived
    case 'in-progress':
      return '#f59e0b' // amber
    case 'completed':
      return '#6b7280' // gray
    case 'cancelled':
      return '#ef4444' // red
    case 'dna':
      return '#dc3545' // red for DNA
    default:
      return '#6b7280'
  }
}

